<template>
  <!-- Pressable cards are toggle buttons (aria-pressed) that apply a filter. -->
  <component
    :is="pressable ? 'button' : 'div'"
    :type="pressable ? 'button' : null"
    class="stat-card"
    :class="[`stat-card--${color}`, { 'stat-card--pressable': pressable, 'stat-card--pressed': pressed }]"
    :aria-pressed="pressable ? pressed : null"
    @click="pressable && $emit('press')"
  >
    <!-- Right side color orb (keep existing) -->
    <div class="stat-card__orb" aria-hidden="true" />


    <div class="stat-card__content">
      <!-- Top row: icon left + label right, trend far right -->
      <div class="stat-card__top-row">
        <component :is="icon" class="stat-card__icon" aria-hidden="true" />
        <p class="stat-card__label">{{ label }}</p>
        <span v-if="trend" class="stat-card__trend" :class="trendClass">
          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 10l7-7 7 7" />
          </svg>
          {{ trend }}
        </span>
      </div>

      <!-- Value -->
      <p class="stat-card__value">{{ displayValue }}</p>

      <!-- Subtitle below value -->
      <p v-if="subtitle" class="stat-card__subtitle">{{ subtitle }}</p>

      <!-- What pressing does, so the card reads as a control -->
      <span v-if="pressable" class="stat-card__action">
        {{ pressed ? pressedActionLabel : actionLabel }}
        <svg v-if="!pressed" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"
          stroke-width="2.5" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </span>
    </div>
  </component>
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";

const props = defineProps({
  label:    { type: String, required: true },
  value:    { type: [Number, String], required: true },
  icon:     { type: Object, required: true },
  subtitle: String,
  trend:    String,
  color:    { type: String, default: "blue" },
  // Turns the card into a toggle button that emits "press".
  pressable: { type: Boolean, default: false },
  pressed:   { type: Boolean, default: false },
  actionLabel:        { type: String, default: "Show" },
  pressedActionLabel: { type: String, default: "Clear" },
});
defineEmits(["press"]);

const displayNum = ref(0);
let rafId = null;

function prefersReducedMotion() {
  return typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

function animateTo(target) {
  if (typeof target !== "number") return;
  cancelAnimationFrame(rafId);
  // The count-up runs on requestAnimationFrame, which the global
  // reduced-motion CSS can't reach — jump straight to the value instead.
  if (prefersReducedMotion()) {
    displayNum.value = target;
    return;
  }
  const start = displayNum.value;
  const diff  = target - start;
  if (diff === 0) return;
  const duration = 900;
  const startTime = performance.now();
  function step(now) {
    const elapsed  = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    displayNum.value = Math.round(start + diff * eased);
    if (progress < 1) rafId = requestAnimationFrame(step);
  }
  rafId = requestAnimationFrame(step);
}

onMounted(() => animateTo(typeof props.value === "number" ? props.value : 0));
watch(() => props.value, (val) => animateTo(typeof val === "number" ? val : 0));

const displayValue = computed(() => {
  if (typeof props.value === "number") return displayNum.value.toLocaleString("en-IN");
  return props.value;
});

const trendClass = computed(() => {
  if (!props.trend) return "";
  if (props.trend.startsWith("+")) return "stat-card__trend--up";
  if (props.trend.startsWith("-")) return "stat-card__trend--down";
  return "stat-card__trend--neutral";
});
</script>

<style scoped>
/* ── Base card ── */
.stat-card {
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  padding: 20px 22px;
  background: #ffffff;
  border: 1px solid #f0f2f5;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05), 0 0 0 0.5px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  cursor: default;
}
.stat-card:hover {
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.08), 0 0 0 0.5px rgba(15, 23, 42, 0.06);
  transform: translateY(-1px);
}

/* ── Pressable (filter toggle) ── */
.stat-card--pressable {
  display: block;
  width: 100%;
  text-align: left;
  font: inherit;
  cursor: pointer;
}
.stat-card--pressed {
  border-color: var(--card-accent);
  box-shadow: 0 0 0 1px var(--card-accent), 0 6px 20px rgba(15, 23, 42, 0.08);
  background: var(--card-tint);
}
.stat-card__action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
  font-size: 12px;
  font-weight: 600;
  color: var(--card-action);
}


/* ── Right orb ── */
.stat-card__orb {
  position: absolute;
  top: -40px; right: -40px;
  width: 140px; height: 140px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--card-orb-from) 0%, transparent 70%);
  opacity: 0.18;
  transition: transform 0.4s ease, opacity 0.4s ease;
  pointer-events: none;
}
.stat-card:hover .stat-card__orb {
  transform: scale(1.35);
  opacity: 0.28;
}

.stat-card__content { position: relative; z-index: 1; }

/* ── Top row: icon + label text + trend ── */
.stat-card__top-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.stat-card__icon {
  width: 20px; height: 20px;
  color: var(--card-accent);
  opacity: 0.85;
  flex-shrink: 0;
}

/* #b0bec5 was 1.9:1 on white; #64748b is 4.76:1. */
.stat-card__subtitle {
  font-size: 12px;
  color: #64748b;
  font-weight: 400;
  margin-top: 3px;
}

/* ── Trend ── */
.stat-card__trend {
  display: inline-flex; align-items: center; gap: 2px;
  font-size: 11px; font-weight: 600;
  white-space: nowrap;
}
.stat-card__trend--up      { color: #059669; }
.stat-card__trend--down    { color: #dc2626; }
.stat-card__trend--neutral { color: #64748b; }

/* ── Value ── */
.stat-card__value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
  color: #0f172a;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  margin-bottom: 5px;
}

/* ── Label ── */
/* Title-style label, matching the rest of the app's headings. */
.stat-card__label {
  flex: 1;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  line-height: 1.2;
}

/* ── Color variants ── */
/* --card-action is the -700 shade so the action text clears 4.5:1 on white. */
.stat-card--blue    { --card-accent: #2563eb; --card-orb-from: #3b82f6; --card-action: #1d4ed8; --card-tint: #f5f9ff; }
.stat-card--emerald { --card-accent: #059669; --card-orb-from: #10b981; --card-action: #047857; --card-tint: #f3fcf8; }
.stat-card--amber   { --card-accent: #d97706; --card-orb-from: #f59e0b; --card-action: #b45309; --card-tint: #fffbf0; }
.stat-card--red     { --card-accent: #dc2626; --card-orb-from: #ef4444; --card-action: #b91c1c; --card-tint: #fff6f6; }
</style>
